// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import "./controllers"

// The service worker (app/views/pwa/service-worker.js) is what makes the app
// installable. It caches nothing but an offline page and answers navigations
// from the network, so registering it everywhere -- development included --
// cannot put a stale page in front of anybody. A failed registration costs the
// install prompt and nothing else, hence the silent catch.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/service-worker.js").catch(() => {})
}

// ---------------------------------------------------------------------------
// DashWind shell chrome.
//
// The DashWind theme ships a 792 KB prebuilt blob (`scripts.js` in the
// intranet app this shell was ported from). We do NOT ship it. Everything the
// chrome actually needs is below: ONE delegated document click handler that
// owns the mobile hamburger (.sidebar-toggle) and the compact toggle
// (.sidebar-compact-toggle); the blob's sidebar accordion is not carried, the
// nav being flat (see the note inside the handler). The
// blob's generic .class-toggle branch was not kept: its only consumer was a
// scrim for a sign-in panel that no control could open.
//
// Delegation is the point. The blob bound its widgets per-element once, at
// module execution, which is correct only in a world where every navigation is
// a full page load. Turbo Drive swaps <body> without re-running the entry
// module, so per-element handlers die with the body they were bound to and the
// next page arrives with a dead sidebar. A single listener on `document`
// survives every DOM replacement there is — Drive visit, snapshot restore,
// turbo frame render, turbo_stream append — so there is nothing to re-bind and
// nothing to leak.
//
// This handler is UNGATED: it owns the widgets from the first paint, because
// there is no second implementation to collide with. (The intranet gates its
// copy behind a `turbo:render`-set body flag precisely because the blob owns
// the widgets on a full load there. That gate has no meaning here — do not
// reintroduce it.)
//
// The logic replicates the blob 1:1 for what is kept — same classes, same
// body-scroll handling.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Hold the scroll position across a Turbo page render.
//
// The layout's `turbo-refresh-scroll: preserve` meta stops Turbo scrolling to
// the top after a save that lands back on the same page (a "replace" visit)
// and after a 422 re-render. That is enough in Chromium. It is NOT enough in
// Safari: WebKit resets window.scrollY to 0 the moment the old <body> is
// replaced, before Turbo makes any scroll decision at all (measured 2026-09-19
// under Playwright's WebKit: 1525 -> 0 with the meta present and Turbo's log
// showing scrollY already 0 at turbo:render). Turbo's own docs pair scroll
// preservation with morphing because a morph never replaces the body; this
// app keeps morph off (see the layout), so the position is put back by hand.
//
// Unconditional on purpose. `turbo:render` fires inside renderPage, BEFORE
// Visit#performScroll and before the 422 path's own scrollToTop, so on every
// visit Turbo still gets the last word wherever it means to scroll: a new page
// goes to the top or its anchor, Back goes to its restored spot. Only where
// Turbo has decided not to scroll -- the preserve cases above -- does the
// re-applied position stand. Page-level events only: frames and streams fire
// their own and never replace <body>.
// ---------------------------------------------------------------------------
let scrollYBeforeRender = null

document.addEventListener('turbo:before-render', function() {
  scrollYBeforeRender = window.scrollY
})

document.addEventListener('turbo:render', function() {
  if (scrollYBeforeRender !== null) window.scrollTo(0, scrollYBeforeRender)
  scrollYBeforeRender = null
})

// Reset transient chrome state before Turbo snapshots the page, or Back/
// Forward resurrects it: an open mobile-sidebar overlay with the body scroll
// locked, and open dropdowns.
document.addEventListener('turbo:before-cache', function() {
  const sidebar = document.querySelector('.nk-sidebar')
  if (sidebar) sidebar.classList.remove('sidebar-visible')
  document.querySelectorAll('.sidebar-toggle.active').forEach(function(t) { t.classList.remove('active') })
  document.body.classList.remove('overflow-hidden')
  document.querySelectorAll('.dropdown-toggle.show').forEach(function(t) { t.classList.remove('show') })
})

// The mobile drawer locks background scroll with `body.overflow-hidden`, and the
// only things that ever took it back off were the hamburger, a scrim tap and the
// before-cache reset above. None of those can fire once the viewport crosses
// `xl`: the sidebar parks on canvas via `xl:translate-x-0` so the drawer LOOKS
// closed, while the scrim turns `xl:invisible` and stops accepting pointer
// events. The lock had no way off, and the page stayed unscrollable at desktop
// width until a full Turbo navigation. Releasing it on the breakpoint crossing
// keeps the drawer's own state in step with what the CSS is showing.
//
// `xl` is 80rem, the Tailwind v4 default; matched with `matchMedia` rather than
// a resize handler so it fires once per crossing instead of on every frame.
const xlQuery = window.matchMedia('(min-width: 80rem)')
function releaseDrawerAboveXl(e) {
  if (!e.matches) return
  const sidebar = document.querySelector('.nk-sidebar')
  if (sidebar) sidebar.classList.remove('sidebar-visible')
  document.querySelectorAll('.sidebar-toggle.active').forEach(function(t) { t.classList.remove('active') })
  document.body.classList.remove('overflow-hidden')
}
xlQuery.addEventListener('change', releaseDrawerAboveXl)

document.addEventListener('click', function(e) {
  // No sidebar accordion. The DashWind blob's wn.toggle / wn.closeSiblings
  // (nested `.nk-menu-toggle` / `.nk-menu-sub` with slideUp/slideDown) was
  // ported in parsons-timesheet and never had a trigger: this shell's nav is
  // flat, as app/views/shared/_sidebar.html.erb says. Dropped here rather than
  // carried as ~80 lines of dead code; the intranet has it if a nested
  // section is ever added, popouts and all.

  // Mobile hamburger — port of the blob's bs.toggle click handler
  const sidebarToggle = e.target.closest('.sidebar-toggle')
  if (sidebarToggle) {
    e.preventDefault()
    sidebarToggle.classList.toggle('active')
    const sidebar = document.querySelector('.nk-sidebar')
    if (sidebar) sidebar.classList.toggle('sidebar-visible')
    document.body.classList.toggle('overflow-hidden')
    return
  }

  // Compact toggle — port of the blob's bs.compact click handler. The blob's
  // has-hover mouseenter binding (expand-on-hover while compact) is NOT ported.
  const compactToggle = e.target.closest('.sidebar-compact-toggle')
  if (compactToggle) {
    e.preventDefault()
    compactToggle.classList.toggle('compact-active')
    const sidebar = document.querySelector('.nk-sidebar')
    if (sidebar) {
      sidebar.classList.toggle('is-compact')
      if (!sidebar.classList.contains('is-compact')) sidebar.classList.remove('has-hover')
    }
    return
  }
})

// ---------------------------------------------------------------------------
// Dropdowns.
//
// Three pieces, and the split between them is deliberate:
//   1. a click-outside closer (bubble phase, document, registered once),
//   2. a delegated .dropdown-toggle opener (CAPTURE phase, document, once),
//   3. hover wiring for the topbar dropdowns, re-run per turbo:load.
// ---------------------------------------------------------------------------

// 0. One place that owns "is this dropdown open". The visual state is the
// `.show` class (the menu is `hidden peer-[.show]:block`); `aria-expanded` has
// to move with it or the topbar toggle lies to a screen reader every time it
// opens. Nothing outside this file should add or remove `.show` by hand.
function setDropdownOpen(toggle, open) {
  toggle.classList.toggle('show', open)
  if (toggle.hasAttribute('aria-expanded')) {
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false')
  }
}

function closeAllDropdowns(except) {
  document.querySelectorAll('.dropdown-toggle.show').forEach(function(toggle) {
    if (toggle !== except) setDropdownOpen(toggle, false)
  })
}

// 1. Close open dropdowns on any click outside a .dropdown. Registered ONCE at
// module scope — never inside a turbo:load listener, where it would stack a new
// document listener on every navigation and leak.
//
// Topbar dropdowns are NO LONGER skipped here. They used to be, because they
// were hover-only and closing them from here fought the hover state; now that a
// tap can open one (piece 2), a tap elsewhere has to be able to close it again,
// and on a mouse this cannot fight anything — clicking outside `.dropdown`
// means the pointer already left the toggle, so pointerleave has closed it.
document.addEventListener('click', function(e) {
  if (!e.target.closest('.dropdown')) closeAllDropdowns()
})

// Escape closes whatever is open. The topbar toggle is a real <button>, so it
// is keyboard-reachable, and a menu you can open from the keyboard but not
// close from the keyboard is a trap.
document.addEventListener('keydown', function(e) {
  if (e.key !== 'Escape') return

  const open = document.querySelector('.dropdown-toggle.show')
  if (!open) return

  setDropdownOpen(open, false)
  if (open.closest('.nk-header')) open.focus()
})

// 2. Card/content dropdown toggles — ONE delegated handler, registered once,
// CAPTURE phase.
//
// Delegation, because these toggles live inside turbo frames and turbo_stream
// appends. Binding them per-element inside a `turbo:load` listener left every
// dropdown rendered by a frame dead: a frame render fires turbo:frame-load,
// NEVER turbo:load. Measured upstream in a Turbo harness against this markup —
// before a frame navigation the toggle toggles `.show`; after one the new
// toggle exists and clicking it does nothing, with turbo:load still at 1.
//
// 🔒 CAPTURE phase is load-bearing, not a style choice. DashWind cards wrap the
// menu in `<div class="dropdown" onclick="event.stopPropagation();">` so a menu
// click never activates the card behind it — which means a bubble-phase
// document delegate never sees these clicks at all. Capture runs before that
// stopPropagation. Do not "simplify" this to a bubble listener.
//
// Topbar dropdowns (.nk-header) reach this handler ONLY when the click did not
// come from a mouse. They are hover-driven for a mouse (piece 3), and letting a
// mouse click through as well would close on the click the menu the hover had
// just opened.
//
// The discriminator is the event's own `pointerType`, not a media query:
// `(hover: hover)` is true on every touchscreen laptop, so a device guess would
// have left exactly the users this fixes — employees on phones — with no way to
// reach Sign Out. A tap reports 'touch', a pen 'pen', and Enter/Space on the
// <button> reports '' (detail 0); only a real mouse reports 'mouse'.
//
// A browser that still dispatches `click` as a MouseEvent (Firefox before 131;
// `allow_browser :modern` admits 121+) reports `undefined`, which this guard
// treats as NOT a mouse — decided in milestone 3. The trade: on those builds a
// mouse user who hovers the menu open and then clicks the toggle closes it
// (the menu's own links still work), while the alternative — counting
// `undefined` as a mouse — would leave touch on those same builds with no way
// to open the menu at all. Touch wins; the hover path is the enhancement.
document.addEventListener('click', function(e) {
  const toggle = e.target.closest('.dropdown-toggle')
  if (!toggle) return

  const dropdown = toggle.closest('.dropdown')
  if (!dropdown) return
  if (dropdown.closest('.nk-header') && e.pointerType === 'mouse') return

  e.preventDefault()
  e.stopPropagation()

  closeAllDropdowns(toggle)
  setDropdownOpen(toggle, !toggle.classList.contains('show'))
}, true)

// 3. Topbar dropdowns are ALSO hover-driven for a mouse, which needs
// per-element enter/leave listeners and a shared hide timer — it cannot be
// expressed as a document delegate. This is an enhancement on top of the click
// handler above, not the only way in.
//
// pointerenter/pointerleave guarded on `pointerType === 'mouse'`, not
// mouseenter/mouseleave: iOS Safari synthesizes mouseenter on a first tap, so
// the old wiring made a tap sometimes open the menu and sometimes not,
// depending on the browser. Guarding on the pointer type makes touch
// deterministic — it never goes through here at all.
//
// Re-run on turbo:load: a Drive visit installs a brand-new
// <body>, so these listeners bind to fresh elements and the old ones are
// garbage with their body. (If this app ever opts into
// `turbo-refresh-method: morph`, revisit — a morph keeps the elements alive and
// this would bind a second set of listeners to them.)
document.addEventListener('turbo:load', function() {
  const topbarDropdowns = document.querySelectorAll('.nk-header .dropdown')

  topbarDropdowns.forEach(function(dropdown) {
    const toggle = dropdown.querySelector('.dropdown-toggle')
    const menu = dropdown.querySelector('.dropdown-menu')
    let hideTimeout = null

    if (!toggle || !menu) return

    function showDropdown() {
      if (hideTimeout) {
        clearTimeout(hideTimeout)
        hideTimeout = null
      }

      closeAllDropdowns(toggle)
      setDropdownOpen(toggle, true)
    }

    // Delay the hide so the pointer can travel from the toggle to the menu
    function hideDropdown() {
      hideTimeout = setTimeout(function() {
        setDropdownOpen(toggle, false)
        hideTimeout = null
      }, 150)
    }

    function cancelHide() {
      if (hideTimeout) {
        clearTimeout(hideTimeout)
        hideTimeout = null
      }
    }

    function onMousePointer(handler) {
      return function(e) {
        if (e.pointerType === 'mouse') handler()
      }
    }

    toggle.addEventListener('pointerenter', onMousePointer(showDropdown))
    toggle.addEventListener('pointerleave', onMousePointer(hideDropdown))
    menu.addEventListener('pointerenter', onMousePointer(cancelHide))
    menu.addEventListener('pointerleave', onMousePointer(hideDropdown))
  })

  // Click-based dropdowns are deliberately NOT initialised here. They belong to
  // the delegated capture-phase listener above; re-adding a per-element binding
  // would double-fire against it and cancel itself out (the toggle would open
  // and immediately close).
})
